import { gql } from "apollo-boost";
import React from "react";
import { useQuery } from "react-apollo-hooks";
import Helmet from "react-helmet-async";
import { Container } from "../../../components/Grid";
import Link from "../../../components/Link";
import { Section } from "../../../components/Sections";
import { fragment as HeaderImageFragment } from "../../producer/HeaderImage";
import { fragments as ProductFragments } from "../Product";
import { fragment as ProductCardFragment } from "../ProductCard";
import ProductsNavigation, {
  findAdjacentProducts
} from "../ProductsNavigation";
import {
  fragments as SectionFragments,
  HeadingSection,
  OtherProductsSection,
  ProductActions,
  ProductBody
} from "./ProductSections";

export default function ListProductPage(props) {
  const { lang, projectId, productId, location: { key } = {} } = props;

  const { data } = useQuery(query, {
    variables: { lang, projectId, productId },
    suspend: true
  });
  const list = data.project || {};
  const product = list.product || {};
  const producer = product.producer || {};
  const allProducts = producer.allProducts || [];

  // Select the 6 products next to the current product id.
  const otherProducts = getOtherProducts(allProducts, productId, 6);
  const { previousProduct, nextProduct } = findAdjacentProducts(
    allProducts,
    productId
  );
  const { id: previousProductId = null } = previousProduct || {};
  const { id: nextProductId = null } = nextProduct || {};
  useQuery(adjacentQuery, {
    variables: { lang, projectId, previousProductId, nextProductId }
  });

  return (
    <div key={key}>
      <Helmet>
        <title>{product.nameWithProducerAndVintage}</title>
      </Helmet>

      <Container>
        <Link variant="back" to="../">
          {list.name}
        </Link>

        <HeadingSection product={product} />

        <Section>
          <ProductsNavigation
            previousProduct={previousProduct}
            nextProduct={nextProduct}
          >
            <ProductActions product={product} />
          </ProductsNavigation>
        </Section>

        <ProductBody product={product} />
      </Container>

      <OtherProductsSection products={otherProducts} producer={producer} />
    </div>
  );
}

const fragment = gql`
  fragment ProductDetails on Product {
    id
    name
    nameWithProducerAndVintage
    ...ProductImage
    ...ProductStand
    ...ProductProducerName
    ...ProductName
    ...ProductVarieties
    producer {
      producerId: id
      producerName {
        shortName
      }
      mainPicture {
        ...HeaderImage
      }
      allProducts: products {
        ...ProductCard
      }
    }
    ...ProductBody
  }
  ${ProductFragments.image}
  ${ProductFragments.stand}
  ${ProductFragments.producerName}
  ${ProductFragments.name}
  ${ProductFragments.varieties}
  ${ProductCardFragment}
  ${SectionFragments.body}
  ${HeaderImageFragment}
`;

export const query = gql`
  query ProductDetails($lang: String, $projectId: ID!, $productId: ID!) {
    project(id: $projectId, lang: $lang) {
      name
      mainPictureFallback: mainPicture {
        ...Image
      }
      product(id: $productId) {
        ...ProductDetails
      }
    }
  }
  ${fragment}
`;

const adjacentQuery = gql`
  query AdjacentProductDetails(
    $lang: String
    $projectId: ID!
    $previousProductId: ID
    $nextProductId: ID
  ) {
    project(id: $projectId, lang: $lang) {
      previousProduct: product(id: $previousProductId) {
        ...ProductDetails
      }
      nextProduct: product(id: $nextProductId) {
        ...ProductDetails
      }
    }
  }
  ${fragment}
`;

function getOtherProducts(allProducts, productId, count) {
  const index = allProducts.indexOf(productId);
  const firstIndex = Math.max(
    0,
    Math.min(index - Math.round(count / 2), allProducts.length - count - 1)
  );
  const lastIndex = Math.max(
    0,
    Math.min(allProducts.length - 1, firstIndex + count)
  );
  const otherProducts = allProducts
    .filter(({ id }) => id !== productId)
    .slice(firstIndex, lastIndex);
  return otherProducts;
}
